"use client";

import React, { useState } from "react";
import { useComponentTranslations } from "@/app/components/LanguageProvider";
import { CONTACTS } from "@/lib/site-contacts";
import { useLocale } from "@/app/components/LocaleProvider";
import { getBaseLocale } from "@/lib/i18n";

type Status = "idle" | "sending" | "success" | "error";

export default function ContactForm() {
  const locale = useLocale();
  const { t } = useComponentTranslations("Contacts");
  const [formData, setFormData] = useState({ name: "", email: "", message: "", website: "" });
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  const isEn = getBaseLocale(locale) === "en";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json().catch(() => null);
      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", message: "", website: "" });
      } else {
        setStatus("error");
        setError(
          typeof data?.error === "string"
            ? data.error
            : (isEn ? "Failed to send the message." : "Не удалось отправить сообщение."),
        );
      }
    } catch {
      setStatus("error");
      setError(isEn ? "Failed to send the message." : "Не удалось отправить сообщение.");
    }
  };

  const inputClass =
    "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-white/30 transition-colors duration-200";

  return (
    <div className="glass-matte glass-hover glass-transition glass-rounded p-8">
      <h2 className="text-2xl font-semibold mb-6">
        {t("formTitle", isEn ? "Write to us" : "Напишите нам")}
      </h2>
      {status === "success" ? (
        <div className="rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-green-300">
          {isEn
            ? `Thank you! Your message has been sent to ${CONTACTS.email}.`
            : `Спасибо! Ваше сообщение отправлено на ${CONTACTS.email}.`}
        </div>
      ) : (
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="hidden" aria-hidden="true">
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-2">
              {t("formName", isEn ? "Name" : "Имя")}
            </label>
            <input
              type="text"
              required
              className={inputClass}
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-2">Email</label>
            <input
              type="email"
              required
              className={inputClass}
              name="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-2">
              {t("formMessage", isEn ? "Your message" : "Ваше сообщение")}
            </label>
            <textarea
              name="message"
              required
              rows={4}
              className={`${inputClass} resize-none`}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            />
          </div>
          {status === "error" && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={status === "sending"}
            className="w-full bg-white text-black font-semibold py-3 rounded-xl hover:bg-white/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === "sending"
              ? (isEn ? "Sending..." : "Отправка...")
              : (isEn ? "Send" : "Отправить")}
          </button>
        </form>
      )}
    </div>
  );
}